import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { TranslocoDirective } from '@jsverse/transloco';
import {
  CartItem,
  CartItemProduct,
  Category,
  DEFAULT_LANGUAGE,
  isCategoryAvailable,
  SUPPORTED_LANGUAGES,
} from '@pedix-workspace/utils';
import {
  CartViewGrouping,
  CartViewProductGroup,
  CartViewSectionHeader,
  getCartViewSections,
  getOptionItemMultiplier,
  getOptionItemQuantity,
  getOptionItemTotal,
  getOrderItemRows,
  OrderItem,
  OrderItemRow,
} from '@pedix-workspace/orders';
import {
  CartCategoryShellComponent,
  CartOrderItemComponent,
  CartOrderItemOptionGroupComponent,
  CartOrderItemOptionItemComponent,
  CartOrderItemRowComponent,
  CartProductGroupShellComponent,
  CartProductHeaderComponent,
  CartViewShellComponent,
} from '@pedix-workspace/angular-ui-cart-view';
import {
  getPromotionI18nParams,
  PromotionInput,
  UpsellHint,
} from '@pedix-workspace/shared-promotions';
import { ModalDialogService } from '@pedix-workspace/angular-ui-modal';
import { CategoryAvailabilityDialogComponent } from '@pedix-workspace/pedixapp-shared-catalog';
import {
  CurrencyFormatPipe,
  PropInSelectedLanguagePipe,
} from '@pedix-workspace/pedixapp-shared-pipes';
import {
  StockInfoPipe,
  StockIsOutOfStockPipe,
  StockWarningPipe,
} from '@pedix-workspace/pedixapp-stock';

@Component({
  selector: 'pxw-cart-view',
  templateUrl: './cart-view.component.html',
  styleUrls: ['./cart-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    TranslocoDirective,
    PropInSelectedLanguagePipe,
    StockInfoPipe,
    StockIsOutOfStockPipe,
    StockWarningPipe,
    CartViewShellComponent,
    CartCategoryShellComponent,
    CartProductGroupShellComponent,
    CartProductHeaderComponent,
    CartOrderItemComponent,
    CartOrderItemRowComponent,
    CartOrderItemOptionGroupComponent,
    CartOrderItemOptionItemComponent,
  ],
})
export class CartViewComponent {
  private modalDialogService = inject(ModalDialogService);
  private currencyFormatPipe = inject(CurrencyFormatPipe);

  readonly cartItems = input.required<CartItem[]>();
  readonly readOnly = input(false);
  readonly selectedLanguage = input<SUPPORTED_LANGUAGES>();
  readonly displaySku = input(false);
  readonly displayImage = input(true);
  readonly grouping = input<CartViewGrouping>('categories-and-groups');
  readonly displaySubtotals = input(true);
  readonly stickyCategoryHeaders = input(false);
  readonly columns = input<number>(1);
  readonly promotionsByProductId = input<Map<string, PromotionInput[]>>(new Map());
  readonly savingsByProductId = input<Map<string, number>>(new Map());
  readonly upsellHintsByCartItemId = input<Map<string, UpsellHint[]>>(new Map());
  readonly categoriesAvailabilityById = input<Record<string, Category['availability']>>({});
  readonly undefinedGroupName = input('');
  readonly restrictionLabelByProductId = input<Map<string, string>>(new Map());
  readonly availableOptionsLabelByProductId = input<Map<string, string>>(new Map());
  readonly displayItemSelector = input(false);
  readonly selectedCartItemIds = input<string[]>([]);

  readonly edit = output<CartItem>();
  readonly remove = output<CartItem>();
  readonly removeAll = output<CartItem[]>();
  readonly update = output<CartItem>();
  readonly itemSelectedChange = output<{ cartItem: CartItem; selected: boolean }>();

  readonly sections = computed(() => getCartViewSections(this.cartItems(), this.grouping()));

  private readonly selectedCartItemIdSet = computed(() => new Set(this.selectedCartItemIds()));

  protected isCartItemSelected(cartItemId: string): boolean {
    return this.selectedCartItemIdSet().has(cartItemId);
  }

  protected getHeaderLabel(header: CartViewSectionHeader | null): string {
    if (!header) {
      return '';
    }
    if (header.isCustomGroup && header.groupName) {
      return `${header.categoryName} (${header.groupName})`;
    }
    if (header.isCustomGroup && !header.groupName) {
      return `${header.categoryName} (${this.undefinedGroupName()})`;
    }
    return header.categoryName;
  }

  protected getCategoryAvailability(categoryId: string): Category['availability'] | undefined {
    return this.categoriesAvailabilityById()[categoryId];
  }

  protected isCategoryAvailable(categoryId: string | null): boolean {
    if (categoryId === null) {
      return true;
    }

    return isCategoryAvailable(this.getCategoryAvailability(categoryId));
  }

  protected getPromotions(productId: string): PromotionInput[] {
    return this.promotionsByProductId().get(productId) ?? [];
  }

  protected getSavings(productId: string): number {
    return this.savingsByProductId().get(productId) ?? 0;
  }

  protected getUpsellHints(cartItemId: string): UpsellHint[] {
    return this.upsellHintsByCartItemId().get(cartItemId) ?? [];
  }

  protected getRowUpsellHint(cartItemId: string, row: OrderItemRow): UpsellHint | undefined {
    return this.getUpsellHints(cartItemId).find(hint => hint.itemId === row.itemId);
  }

  protected buildPromoTagLabels(
    promotions: PromotionInput[],
    t: (key: string, params?: Record<string, unknown>) => string,
  ): string[] {
    const lang = this.selectedLanguage() ?? DEFAULT_LANGUAGE;
    return promotions.map(promo =>
      t(`promotionTag.${promo.discountType}`, getPromotionI18nParams(promo, lang)),
    );
  }

  protected formatCurrency(value: number): string {
    return this.currencyFormatPipe.transform(value);
  }

  protected shouldDisplaySubtotal(productGroup: CartViewProductGroup, savings: number): boolean {
    if (!this.displaySubtotals()) {
      return false;
    }

    const hasMultiple = productGroup.orderItems.length > 1;
    const hasPriced = productGroup.orderItems.some(orderItem =>
      orderItem.cartItem.selectedOptions?.some(opt =>
        opt.items.some(item => (item.price ?? 0) > 0),
      ),
    );
    return hasMultiple || savings > 0 || hasPriced;
  }

  protected getRows(orderItem: OrderItem): OrderItemRow[] {
    return getOrderItemRows(orderItem);
  }

  protected getOptionMultiplier(orderItem: OrderItem): number {
    return getOptionItemMultiplier(orderItem.cartItem);
  }

  protected getOptionItemQty(
    option: { rangeType: string; min?: number; displayItemQuantities?: boolean },
    item: { quantity?: number },
    multiplier: number,
  ): number {
    return getOptionItemQuantity(option as never, item as never, multiplier);
  }

  protected getOptionItemTotalAmount(
    option: { rangeType: string; min?: number; displayItemQuantities?: boolean },
    item: { price?: number; priceDiscount?: number; quantity?: number },
    multiplier: number,
  ): number {
    return getOptionItemTotal(option as never, item as never, multiplier, {
      usePriceDiscount: true,
    });
  }

  protected hasOptions(orderItem: OrderItem): boolean {
    return (orderItem.cartItem.selectedOptions ?? []).length > 0;
  }

  protected getProductFromGroup(productGroup: CartViewProductGroup): CartItemProduct | undefined {
    return productGroup.orderItems[0]?.cartItem.product;
  }

  protected isProductGroupHidden(productGroup: CartViewProductGroup): boolean {
    return productGroup.orderItems.some(orderItem => !!orderItem.cartItem.product.hidden);
  }

  protected getProductFirstImage(product: CartItemProduct): string | undefined {
    return product.images?.[0];
  }

  protected onRemoveProductGroup(productGroup: CartViewProductGroup) {
    const items = productGroup.orderItems.map(orderItem => orderItem.cartItem);
    this.removeAll.emit(items);
  }

  protected onRowQtyChange(cart: CartItem, row: OrderItemRow, newQty: number) {
    if (row.presentationIndex === null) {
      this.update.emit({ ...cart, qty: newQty });
      return;
    }
    const selectedPresentation = cart.selectedPresentation;
    if (!selectedPresentation) {
      return;
    }
    const items = selectedPresentation.items.map((item, index) =>
      index === row.presentationIndex ? { ...item, quantity: newQty } : item,
    );
    this.update.emit({
      ...cart,
      selectedPresentation: { ...selectedPresentation, items },
    });
  }

  protected onOpenAvailability(categoryId: string) {
    const availability = this.getCategoryAvailability(categoryId);
    if (!availability) {
      return;
    }
    this.modalDialogService
      .attachDialog(CategoryAvailabilityDialogComponent)
      .open({ availability });
  }
}
